import type { StoreApi, UseBoundStore } from 'zustand';
import { PrefSwitch } from '../components/PrefControls';
import type { ShellPreferencesStoreState } from '../shell/createShellPreferencesStore';

export interface ShellPreferencesPanelProps {
  useShellPreferencesStore: UseBoundStore<StoreApi<ShellPreferencesStoreState>>;
}

export function ShellPreferencesPanel({ useShellPreferencesStore }: ShellPreferencesPanelProps) {
  const autoHideSideMenu = useShellPreferencesStore((s) => s.autoHideSideMenu);
  const setAutoHideSideMenu = useShellPreferencesStore((s) => s.setAutoHideSideMenu);
  const showNavMenuSearch = useShellPreferencesStore((s) => s.showNavMenuSearch);
  const setShowNavMenuSearch = useShellPreferencesStore((s) => s.setShowNavMenuSearch);
  const showNavExpandAll = useShellPreferencesStore((s) => s.showNavExpandAll);
  const setShowNavExpandAll = useShellPreferencesStore((s) => s.setShowNavExpandAll);
  const showNavCollapseAll = useShellPreferencesStore((s) => s.showNavCollapseAll);
  const setShowNavCollapseAll = useShellPreferencesStore((s) => s.setShowNavCollapseAll);

  return (
    <section
      className="theme-preferences theme-preferences--first"
      aria-labelledby="shell-preferences-heading"
    >
      <h3 id="shell-preferences-heading" className="theme-preferences__title">
        Navigation
      </h3>
      <p className="theme-preferences__hint">
        On wide screens with a mouse, control whether the left navigation stays visible. Narrow
        windows (1024px or less) use a slide-out menu — tap the site logo in the header to open it.
      </p>
      <div className="theme-preferences__section">
        <div className="theme-preferences__compact-row">
          <PrefSwitch checked={autoHideSideMenu} onChange={setAutoHideSideMenu} aria-label="Auto-hide Side Menu" />
          <span className="theme-preferences__hint theme-preferences__hint--inline">
            Auto-hide Side Menu
          </span>
        </div>
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          When enabled on desktop, the side menu stays collapsed until you move the pointer over the
          left edge; it hides again when the pointer leaves. This does not apply on compact layouts.
        </p>
      </div>
      <div className="theme-preferences__section">
        <span className="theme-preferences__label">Side menu controls</span>
        <p className="theme-preferences__hint">
          Optional buttons in the side menu. Each stays hidden until you turn it on here.
        </p>
        <div className="theme-preferences__switch-stack">
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={showNavMenuSearch} onChange={setShowNavMenuSearch} aria-label="Search menu" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Search menu
            </span>
          </div>
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={showNavExpandAll} onChange={setShowNavExpandAll} aria-label="Expand all" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Expand all
            </span>
          </div>
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={showNavCollapseAll} onChange={setShowNavCollapseAll} aria-label="Collapse all" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Collapse all
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
